import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Phone,
  Send,
  CheckCheck,
  Check,
  Languages,
  Smile,
  Image as ImageIcon,
} from 'lucide-react';
import type { DemoChatMessage } from '@/data/demoMockData';

interface DemoChatScreenProps {
  isOpen: boolean;
  onClose: () => void;
  messages: DemoChatMessage[];
  hostName: string;
  darkMode: boolean;
  showTyping?: boolean;
}

export function DemoChatScreen({
  isOpen,
  onClose,
  messages,
  hostName,
  darkMode,
  showTyping = false,
}: DemoChatScreenProps) {
  const { t } = useTranslation();
  const [draftMessage, setDraftMessage] = useState('');
  const [showOriginalMap, setShowOriginalMap] = useState<Record<string, boolean>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isOpen, showTyping]);

  if (!isOpen) return null;

  const formatTime = (timestamp: string) => {
    const d = new Date(timestamp);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const toggleOriginal = (id: string) => {
    setShowOriginalMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="absolute inset-0 z-[60] flex flex-col">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Chat Panel - bottom sheet */}
      <div
        className={`absolute bottom-0 left-0 right-0 max-h-[88%] rounded-t-3xl flex flex-col shadow-2xl animate-in slide-in-from-bottom duration-300 ${
          darkMode ? 'bg-slate-900' : 'bg-white'
        }`}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-2 pb-1">
          <div
            className={`w-10 h-1 rounded-full ${
              darkMode ? 'bg-slate-600' : 'bg-gray-300'
            }`}
          />
        </div>

        {/* Header */}
        <div
          className={`flex items-center gap-3 px-4 py-3 border-b ${
            darkMode ? 'border-slate-800' : 'border-gray-100'
          }`}
        >
          <button
            onClick={onClose}
            className={`p-2 rounded-full ${
              darkMode ? 'hover:bg-slate-800 text-slate-300' : 'hover:bg-gray-100 text-gray-600'
            }`}
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="relative">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-bold text-sm shadow-md">
              {hostName.charAt(0)}
            </div>
            <span
              className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-500 ring-2 ${
                darkMode ? 'ring-slate-900' : 'ring-white'
              }`}
            />
          </div>

          <div className="flex-1 min-w-0">
            <h3
              className={`font-bold text-sm truncate ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              {hostName}
            </h3>
            <p
              className={`text-[11px] ${
                darkMode ? 'text-green-400' : 'text-green-600'
              }`}
            >
              Online
            </p>
          </div>

          <button
            className={`p-2 rounded-full ${
              darkMode ? 'hover:bg-slate-800 text-cyan-400' : 'hover:bg-gray-100 text-cyan-600'
            }`}
            aria-label="Call host"
          >
            <Phone className="w-5 h-5" />
          </button>
        </div>

        {/* Translation banner */}
        <div
          className={`px-4 py-2 flex items-center gap-2 text-xs ${
            darkMode
              ? 'bg-cyan-500/10 text-cyan-300 border-b border-slate-800'
              : 'bg-cyan-50 text-cyan-700 border-b border-cyan-100'
          }`}
        >
          <Languages className="w-3.5 h-3.5" />
          <span className="font-medium">
            {t('demo.autoTranslate', { from: 'Hrvatski', to: 'Polski' })}
          </span>
        </div>

        {/* Messages */}
        <div
          className={`flex-1 overflow-y-auto px-4 py-4 space-y-3 ${
            darkMode
              ? 'bg-gradient-to-b from-slate-900 to-slate-950'
              : 'bg-gradient-to-b from-gray-50 to-white'
          }`}
          style={{ maxHeight: 'calc(88vh - 220px)' }}
        >
          {messages.map((msg, idx) => {
            const isLandlord = msg.senderType === 'landlord';
            const showAvatar =
              isLandlord &&
              (idx === 0 || messages[idx - 1]?.senderType !== 'landlord');
            const showingOriginal = showOriginalMap[msg.id];
            // For landlord messages, the displayed translation is what guest sees
            // For guest messages (own), they see their original
            const displayContent = isLandlord
              ? showingOriginal
                ? msg.originalContent
                : msg.translatedContent
              : msg.originalContent;
            const wasTranslated = isLandlord; // Landlord wrote in HR, guest reads in PL

            return (
              <div
                key={msg.id}
                className={`flex ${isLandlord ? 'justify-start' : 'justify-end'}`}
              >
                {/* Avatar slot */}
                {isLandlord && (
                  <div className="w-8 mr-2 shrink-0">
                    {showAvatar && (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-bold text-xs shadow-sm">
                        {hostName.charAt(0)}
                      </div>
                    )}
                  </div>
                )}

                <div className="max-w-[78%]">
                  {/* Bubble */}
                  <div
                    className={`px-4 py-2.5 rounded-[20px] shadow-sm ${
                      isLandlord
                        ? darkMode
                          ? 'bg-slate-800 text-white rounded-bl-md'
                          : 'bg-white border border-gray-100 text-gray-900 rounded-bl-md'
                        : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-br-md shadow-blue-500/20'
                    }`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                      {displayContent}
                    </p>

                    {/* Show original toggle for landlord messages */}
                    {wasTranslated && (
                      <button
                        onClick={() => toggleOriginal(msg.id)}
                        className={`flex items-center gap-1 mt-1.5 text-[10px] transition-colors ${
                          darkMode
                            ? 'text-slate-400 hover:text-slate-200'
                            : 'text-gray-400 hover:text-gray-600'
                        }`}
                      >
                        <Languages className="w-3 h-3" />
                        {showingOriginal
                          ? t('chat.showTranslation', 'Prikaži prijevod')
                          : t('chat.showOriginal', 'Prikaži original')}
                      </button>
                    )}

                    {/* Time + Read receipt */}
                    <div
                      className={`flex items-center justify-end gap-1 mt-1 ${
                        isLandlord
                          ? darkMode
                            ? 'text-slate-500'
                            : 'text-gray-400'
                          : 'text-blue-100'
                      }`}
                    >
                      <span className="text-[10px]">
                        {formatTime(msg.timestamp)}
                      </span>
                      {!isLandlord &&
                        (msg.isRead ? (
                          <CheckCheck className="w-3.5 h-3.5 text-blue-200" />
                        ) : (
                          <Check className="w-3.5 h-3.5 text-blue-100/80" />
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Typing indicator */}
          {showTyping && (
            <div className="flex justify-start">
              <div className="w-8 mr-2 shrink-0">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-bold text-xs">
                  {hostName.charAt(0)}
                </div>
              </div>
              <div
                className={`px-4 py-3 rounded-[20px] rounded-bl-md ${
                  darkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
                }`}
              >
                <div className="flex gap-1">
                  <span
                    className={`w-2 h-2 rounded-full animate-bounce ${
                      darkMode ? 'bg-slate-400' : 'bg-gray-400'
                    }`}
                    style={{ animationDelay: '0ms' }}
                  />
                  <span
                    className={`w-2 h-2 rounded-full animate-bounce ${
                      darkMode ? 'bg-slate-400' : 'bg-gray-400'
                    }`}
                    style={{ animationDelay: '150ms' }}
                  />
                  <span
                    className={`w-2 h-2 rounded-full animate-bounce ${
                      darkMode ? 'bg-slate-400' : 'bg-gray-400'
                    }`}
                    style={{ animationDelay: '300ms' }}
                  />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div
          className={`px-3 py-3 border-t ${
            darkMode ? 'border-slate-800 bg-slate-900' : 'border-gray-100 bg-white'
          }`}
        >
          <div
            className={`flex items-center gap-1 px-2 py-1.5 rounded-2xl ${
              darkMode ? 'bg-slate-800' : 'bg-gray-100'
            }`}
          >
            <button
              className={`p-2 rounded-full ${
                darkMode ? 'text-slate-400' : 'text-gray-500'
              }`}
              aria-label="Emoji"
            >
              <Smile className="w-5 h-5" />
            </button>
            <button
              className={`p-2 rounded-full ${
                darkMode ? 'text-slate-400' : 'text-gray-500'
              }`}
              aria-label="Image"
            >
              <ImageIcon className="w-5 h-5" />
            </button>
            <input
              type="text"
              value={draftMessage}
              onChange={(e) => setDraftMessage(e.target.value)}
              placeholder={t('demo.tryChat', 'Isprobajte chat...')}
              className={`flex-1 bg-transparent text-sm outline-none px-2 ${
                darkMode
                  ? 'text-white placeholder-slate-500'
                  : 'text-gray-900 placeholder-gray-400'
              }`}
            />
            <button
              className={`p-2.5 rounded-full transition-all ${
                draftMessage
                  ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-md shadow-blue-500/30'
                  : darkMode
                  ? 'bg-slate-700 text-slate-500'
                  : 'bg-gray-200 text-gray-400'
              }`}
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DemoChatScreen;
